import React from "react";
import { BookOpen, Mic, X as XIcon, ChevronRight, Compass, Film, Watch, Coins } from "lucide-react";

/**
 * GuideCenter — Centro Guida di TIME INSIDE
 *
 * È il punto unico di accesso a tutti i popup informativi dell'app.
 *
 * Filosofia:
 *  - MODULARE: la lista di voci è definita da un array `entries` passato dal
 *    parent (o costruito qui con default noti). Aggiungere una nuova voce
 *    significa aggiungere un oggetto all'array — senza toccare la struttura.
 *  - NON DISTRUTTIVO: il flag "Non mostrare più" di ciascun popup blocca
 *    solo l'auto-apertura; il popup resta sempre richiamabile da qui.
 *  - COERENTE: stile "Apple-like" allineato al TimeWatchWelcomeDialog.
 *
 * v1 include:
 *  📖 Libretto di Benvenuto
 *  🎙️ Comandi Vocali
 *
 * Predisposizione futura: Tour Guidato, Guarda il Film, Novità, Suggerimenti…
 * Basta estendere l'array di entries.
 */

const ICONS = {
  book: BookOpen,
  mic: Mic,
  compass: Compass,
  film: Film,
  watch: Watch,
  coins: Coins,
};

export default function GuideCenter({ open, onClose, entries = [] }) {
  if (!open) return null;

  return (
    <div
      data-testid="guide-center"
      className="fixed inset-0 z-[65] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="glass-card relative w-full max-w-lg p-6 sm:p-8 border-emerald-400/25 shadow-[0_25px_80px_-20px_rgba(16,185,129,0.35)] max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="guide-center-close"
          aria-label="Chiudi Centro Guida"
          className="absolute top-4 right-4 p-1 rounded-full text-white/50 hover:text-white hover:bg-white/5 transition"
        >
          <XIcon className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 rounded-full bg-emerald-400/10 border border-emerald-400/25 flex items-center justify-center text-emerald-300">
            <Compass className="w-4 h-4" />
          </div>
          <div className="text-[10px] tracking-[0.32em] uppercase text-emerald-300">
            TIME INSIDE
          </div>
        </div>
        <h2
          data-testid="guide-center-title"
          className="text-2xl sm:text-3xl font-light text-white tracking-tight leading-tight mb-2"
        >
          Centro Guida
        </h2>
        <p className="text-sm font-light text-white/55 leading-relaxed mb-6">
          Tutti i popup informativi dell'ecosistema TIME, sempre a portata di mano.
        </p>

        {/* Lista voci */}
        <ul data-testid="guide-center-entries" className="space-y-3">
          {entries.map((entry) => {
            const IconCmp = ICONS[entry.icon] || BookOpen;
            return (
              <li key={entry.id}>
                <button
                  type="button"
                  onClick={() => entry.onOpen && entry.onOpen()}
                  data-testid={`guide-center-entry-${entry.id}`}
                  className="group w-full flex items-center gap-4 text-left px-4 py-4 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-emerald-400/35 hover:bg-emerald-400/[0.04] transition"
                >
                  <div className="w-11 h-11 rounded-xl bg-emerald-400/10 border border-emerald-400/20 flex items-center justify-center text-emerald-300 shrink-0 group-hover:bg-emerald-400/15 transition">
                    <IconCmp className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm sm:text-base font-light text-white leading-snug">
                      {entry.title}
                    </div>
                    {entry.description && (
                      <div className="mt-0.5 text-[11px] font-light text-white/45 leading-relaxed">
                        {entry.description}
                      </div>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-emerald-300 group-hover:translate-x-0.5 transition shrink-0" />
                </button>
              </li>
            );
          })}
          {entries.length === 0 && (
            <li
              data-testid="guide-center-empty"
              className="px-4 py-6 text-center text-white/40 text-sm font-light"
            >
              Nessuna guida disponibile al momento.
            </li>
          )}
        </ul>

        <div className="mt-6 text-[11px] text-white/30 leading-relaxed">
          Nuove voci verranno aggiunte man mano che l'ecosistema TIME evolve.
        </div>
      </div>
    </div>
  );
}
